import {
  createContext,
  ReactNode,
  useReducer,
  useState,
  SetStateAction,
  Dispatch,
} from "react";

import get from "lodash/get";
import intersection from "lodash/intersection";

import { Role, UserFragment } from "~graphql/sdk";

import * as track from "~features/analytics/track";

const USER_LOGIN_STATES = [
  "unknown",
  "authenticated",
  "unauthenticated",
  "requires-2fa",
] as const;
type UserAuthState = typeof USER_LOGIN_STATES[number];

export enum UserActionTypes {
  LOGIN = "LOGIN",
  LOGOUT = "LOGOUT",
  AUTHENTICATE_TWO_FACTOR = "AUTHENTICATE_TWO_FACTOR",
}

type UserActions =
  | { type: UserActionTypes.LOGOUT }
  | { type: UserActionTypes.LOGIN; payload: UserFragment }
  | { type: UserActionTypes.AUTHENTICATE_TWO_FACTOR };

interface UserState {
  user: UserFragment;
  isInitialising: boolean;
  isLoggedIn: boolean;
  hasLoaded: boolean;
  isTwoFactorAuthenticated: boolean;
  authState: UserAuthState;
}

export type UserRoles = Record<keyof typeof Role, boolean>;

interface UserContextProps extends UserState {
  dispatch: (input: UserActions) => void;
  isAuth: boolean;
  isAdmin: boolean;
  isPOS: boolean;
  hasCheckedLogin: boolean;
  setHasCheckedLogin: Dispatch<SetStateAction<boolean>>;
  isSalesOutlet: boolean;
  isEventManager: boolean;
  userRoles: UserRoles;
}

interface Props {
  children: ReactNode;
}

const initialState: UserState = {
  isInitialising: true,
  isLoggedIn: false,
  user: undefined,
  hasLoaded: false,
  isTwoFactorAuthenticated: false,
  authState: "unknown",
};

const hasRoles = (
  userRoles: UserFragment["roles"],
  roles: UserFragment["roles"]
) => Boolean(get(intersection(userRoles ?? [], roles), "length"));

const getIsTwoFactorAuthenticatedInLocalStorage = () => {
  return !!localStorage.getItem("isTwoFactorAuthenticated");
};

const setIsTwoFactorAuthenticatedInLocalStorage = () => {
  localStorage.setItem("isTwoFactorAuthenticated", "true");
};

const removeIsTwoFactorAuthenticatedInLocalStorage = () => {
  localStorage.removeItem("isTwoFactorAuthenticated");
};

function reducer(state: UserState, action: UserActions) {
  switch (action.type) {
    case UserActionTypes.LOGOUT:
      removeIsTwoFactorAuthenticatedInLocalStorage();

      track.logout();

      return {
        ...state,
        isInitialising: false,
        user: undefined,
        isLoggedIn: false,
        hasLoaded: true,
        isTwoFactorAuthenticated: false,
        authState: "unauthenticated",
      };
    case UserActionTypes.LOGIN:
      track.login(action.payload);

      return {
        ...state,
        isInitialising: false,
        user: action.payload,
        isLoggedIn: true,
        hasLoaded: true,
        authState:
          action.payload.isTwoFactorAuthenticationEnabled &&
          !getIsTwoFactorAuthenticatedInLocalStorage()
            ? "requires-2fa"
            : "authenticated",
        isTwoFactorAuthenticated: getIsTwoFactorAuthenticatedInLocalStorage(),
      };
    case UserActionTypes.AUTHENTICATE_TWO_FACTOR:
      setIsTwoFactorAuthenticatedInLocalStorage();
      return {
        ...state,
        isInitialising: false,
        isTwoFactorAuthenticated: true,
        authState: "authenticated",
      };
    default:
  }
}

const UserContext = createContext<UserContextProps>(undefined);

const UserProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [hasCheckedLogin, setHasCheckedLogin] = useState(false);

  return (
    <UserContext.Provider
      //  @ts-expect-error problem with authState recognizing a string union
      value={{
        ...state,
        hasCheckedLogin,
        setHasCheckedLogin,
        dispatch,
        isAuth: !!state?.user,
        isPOS: hasRoles(state?.user?.roles, [Role.PosOperator]),
        isAdmin: hasRoles(state?.user?.roles, [
          Role.Admin,
          Role.Superadmin,
          Role.Sales,
        ]),
        isSalesOutlet: hasRoles(state?.user?.roles, [Role.SalesOutlet]),
        isEventManager: hasRoles(state?.user?.roles, [Role.EventManager]),
        userRoles: generateUserRoles(state?.user?.roles, Role),
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export { UserProvider, UserContext };

export function generateUserRoles(
  userRoles: UserFragment["roles"] = [],
  allRoles: typeof Role
) {
  return (Object.keys(allRoles) as (keyof typeof Role)[]).reduce((acc, key) => {
    const role = Role[key];
    acc[key] = hasRoles(userRoles, [role]);
    return acc;
  }, {}) as UserContextProps["userRoles"];
}
